import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { getTenantId, getDepartmentId } from 'src/app/services/store-snapshot.service';
import { FormDataService } from 'src/app/shared/attachments/service/form-data.service';
import { IdName } from 'src/app/shared/models/id-name';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { environment } from 'src/environments/environment';
import { ALL_DEPS_TOKEN } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { OrderStatus } from '../enums/order-status.enum';
import { IOrderDraftDefaults } from '../models/order-draft-defaults.model';
import { IOrderResponse, IOrderRequest } from '../models/order.model';
import { IReceiveOrderLine } from '../models/receive-order-line.model';

@Injectable({
  providedIn: 'root',
})
export class OrderApiService {
  headers = { 'content-type': 'application/json' };

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/orders`;
  }
  private get allDepsUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/orders`;
  }

  constructor(private http: HttpClient) {}

  getOrders(
    orderStage: OrderStatus,
    currentFilter?: MainTableFilterModel,
    allDeps?: boolean
  ): Observable<ApiResponse<IOrderResponse[]>> {
    const url = allDeps ? this.allDepsUrl : this.url;
    let params = createFilterParams(currentFilter);
    params = params.set('OrderStage', orderStage);
    if (allDeps) {
      params = params.set('ExcludeArchived', 'true');
    }

    return this.http.get<ApiResponse<IOrderResponse[]>>(url, { params });
  }

  getAllOrders(): Observable<IdName[]> {
    return this.http.get<IdName[]>(this.url);
  }

  getAllOrdersForDepartment(): Observable<IdName[]> {
    const url = `${
      environment.apiUrl
    }/api/${getTenantId()}/${getDepartmentId()}/departmentScope/orders`;
    return this.http.get<IdName[]>(url);
  }

  getOrderById(orderId: string, orderDepartmentId?: string): Observable<IOrderResponse> {
    let url = '';
    if (orderDepartmentId) {
      url = `${environment.apiUrl}/api/${getTenantId()}/${orderDepartmentId}/orders/${orderId}`;
    } else {
      url = `${this.url}/${orderId}`;
    }

    return this.http.get<IOrderResponse>(url);
  }

  getDraftDefaults(): Observable<IOrderDraftDefaults> {
    const url = `${this.url}/draft-defaults`;
    return this.http.get<IOrderDraftDefaults>(url);
  }

  createOrder(order: IOrderRequest): Observable<IOrderResponse> {
    const formData = FormDataService.mapToFormData(order);
    return this.http.post<IOrderResponse>(this.url, formData);
  }

  duplicateOrder(orderId: string): Observable<IdName> {
    const url = `${this.url}/${orderId}/duplicate`;
    return this.http.post<IdName>(url, null);
  }

  updateOrder(order: IOrderRequest): Observable<IOrderResponse> {
    const formData = FormDataService.mapToFormData(order);
    const url = `${this.url}/${order.id}`;
    return this.http.put<IOrderResponse>(url, formData);
  }

  deleteOrder(id: string) {
    const url = `${this.url}/${id}`;
    return this.http.delete<IOrderResponse>(url);
  }

  printOrderPdf(orderId: string): Observable<Blob> {
    const url = `${this.url}/${orderId}/pdf`;
    return this.http.get(url, { responseType: 'blob' });
  }

  printOrderExcel(orderId: string): Observable<Blob> {
    const url = `${this.url}/${orderId}/excel`;
    return this.http.get(url, { responseType: 'blob' });
  }

  // STAGES endpoints

  sendOrderToDraft(orderId: string): Observable<any> {
    const url = `${this.url}/${orderId}/stages/draft`;
    return this.http.patch<any>(url, { headers: this.headers });
  }

  sendOrderForApproval(orderId: string): Observable<any> {
    const url = `${this.url}/${orderId}/stages/forApprove`;
    return this.http.patch<any>(url, { headers: this.headers });
  }

  approveOrder(orderId: string): Observable<any> {
    const url = `${this.url}/${orderId}/stages/approved`;
    return this.http.patch<any>(url, { headers: this.headers });
  }

  sendOrderToSupplier(
    orderId: string,
    sendEmailManually: boolean,
    addPurchaserContactInCC: boolean
  ): Observable<any> {
    const url = `${this.url}/${orderId}/stages/sentToSupplier`;
    return this.http.patch<any>(
      url,
      { sendEmailManually, addPurchaserContactInCC },
      { headers: this.headers }
    );
  }

  receiveOrder(orderId: string, receivedOrderLines: IReceiveOrderLine[]): Observable<any> {
    const url = `${this.url}/${orderId}/stages/receive`;
    return this.http.patch<any>(url, { receivedOrderLines }, { headers: this.headers });
  }

  cancelOrder(orderId: string): Observable<any> {
    const url = `${this.url}/${orderId}/stages/cancelled`;
    return this.http.patch<any>(url, { headers: this.headers });
  }

  reopenOrder(orderId: string): Observable<any> {
    const url = `${this.url}/${orderId}/stages/reopen`;
    return this.http.patch<any>(url, { headers: this.headers });
  }

  printReport(filter?: MainTableFilterModel, menuId?: string): Observable<Blob> {
    const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
    let url = '';
    if (allDeps) {
      url = `${this.allDepsUrl}/Report`;
    } else {
      url = `${this.url}/Report`;
    }

    let params = createFilterParams(filter);
    params = params.delete('Page');
    params = params.delete('PageSize');
    if (menuId) {
      params = params.set('MenuId', menuId);
    }
    return this.http.get(url, { params, responseType: 'blob' });
  }
}
